import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Checkbox, CheckboxProps } from 'semantic-ui-react';
import {
  ICalendarFilterOption,
  ISavedFilterOptions
} from 'model/calendar/filters';
import { Divider } from 'antd';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FilterPageNameEnum } from 'components/calendar/calendarToolbar/FilterTagList/form/utils';
import { searchValueContext } from 'components/calendar/filters';

interface Props {
  filters: ICalendarFilterOption[];
  selectSavedFilter: (name: string) => void;
  savedFilter: string;
  defaultFilter?: ISavedFilterOptions;
  handleSearchedValues: (
    list: ICalendarFilterOption[],
    checkedList: ICalendarFilterOption[]
  ) => ICalendarFilterOption[];
  setHasData: Dispatch<SetStateAction<boolean>>;
  setClickedFilter: (label: string) => void;
  pageName: string;
  collapsible?: boolean;
}

const SavedFilters = ({
  filters,
  selectSavedFilter,
  savedFilter,
  defaultFilter,
  handleSearchedValues,
  setHasData,
  setClickedFilter,
  pageName,
  collapsible
}: Props) => {
  const [checkedFilter, setCheckedFilter] = useState<string>(savedFilter);
  const searchValue = useContext(searchValueContext);
  //a section with a selected saved filter starts expanded, others collapsed
  const [expanded, setExpanded] = useState(!collapsible || savedFilter !== '');
  const isBodyVisible = !collapsible || expanded || !isEmpty(searchValue);
  //a saved filter arriving after mount (async default filter) must also
  //expand the section; collapsing is only ever done by the user
  useEffect(() => {
    if (collapsible && savedFilter !== '') setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilter]);

  const savedFilters = useMemo(() => {
    const searchedValues = handleSearchedValues(filters, [
      { label: checkedFilter, value: checkedFilter }
    ]);

    return searchedValues;
  }, [handleSearchedValues, filters, checkedFilter]);

  const handleChange = (
    _e: React.FormEvent<HTMLInputElement>,
    data: CheckboxProps
  ) => {
    selectSavedFilter(data.label as string);
    setCheckedFilter(data.label as string);
  };

  useEffect(() => {
    savedFilters?.length === 0 ? setHasData(false) : setHasData(true);
  }, [savedFilters, setHasData]);

  useEffect(() => {
    setCheckedFilter(savedFilter);
  }, [savedFilter]);

  return (
    <>
      {savedFilters?.length > 0 && (
        <>
          {collapsible ? (
            <div
              className="section-header"
              onClick={() => setExpanded(prev => !prev)}
            >
              <div className="title">Saved</div>
              <span className="section-header_controls">
                {checkedFilter !== '' && (
                  <span className="section-badge">1</span>
                )}
                <FontAwesomeIcon
                  className="section-chevron"
                  icon={isBodyVisible ? faChevronUp : faChevronDown}
                />
              </span>
            </div>
          ) : (
            <div className="title">Saved</div>
          )}
          {isBodyVisible &&
            savedFilters.map(filter => {
            return (
              <div className="saved_filters" key={filter.label}>
                <Checkbox
                  className="saved-filters_checkbox"
                  label={filter.label}
                  name={filter.label}
                  value={filter.value}
                  checked={checkedFilter === filter.label}
                  onChange={handleChange}
                />
                <button
                  className="edit_btn"
                  onClick={() => {
                    setClickedFilter(filter.label);
                  }}
                >
                  {defaultFilter?.name === filter.label &&
                  pageName === FilterPageNameEnum.planner
                    ? 'Edit Default'
                    : 'Edit'}
                </button>
              </div>
            );
          })}
          <Divider />
        </>
      )}
    </>
  );
};
export default React.memo(SavedFilters);
